import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageHero from '../../components/ui/page-hero';
import TestimonialDetail from '../../components/testimonials/TestimonialDetail';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Users } from 'lucide-react';

const TestimonialDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [testimonial, setTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('/content/testimonials/testimonials.json');
        const data = await response.json();

        const foundTestimonial = data.testimonials.find(t => t.id === id);
        if (foundTestimonial) {
          setTestimonial(foundTestimonial);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading testimonial...</p>
        </div>
      </div>
    );
  }

  if (!testimonial) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Testimonial Not Found
          </h2>
          <p className="text-muted-foreground mb-6">
            The testimonial you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/testimonials')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Testimonials
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{testimonial.clientCompany} - {testimonial.clientName} | Envael Success Story</title>
        <meta name="description" content={`Read how ${testimonial.clientCompany} transformed their business with Envael's ${testimonial.projectType}. ${testimonial.testimonial}`} />
        <meta name="keywords" content={`${testimonial.clientCompany}, ${testimonial.clientIndustry}, ${testimonial.projectType}, Envael testimonial, success story`} />
        <meta property="og:title" content={`${testimonial.clientCompany} - ${testimonial.clientName} | Envael Success Story`} />
        <meta property="og:description" content={testimonial.testimonial} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={testimonial.clientAvatar} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${testimonial.clientCompany} - ${testimonial.clientName} | Envael Success Story`} />
        <meta name="twitter:description" content={testimonial.testimonial} />
        <meta name="twitter:image" content={testimonial.clientAvatar} />
      </Helmet>

      <PageHero
        pageName={`${testimonial.clientCompany} Success Story`}
        subtitle={`How ${testimonial.clientName} transformed their business with Envael's ${testimonial.projectType}`}
        size="large"
        fullHeight={false}
        enableSpline={true}
      />

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <TestimonialDetail testimonial={testimonial} />
        </div>
      </div>
    </>
  );
};

export default TestimonialDetailPage;
